import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  initiateSalesforceOAuth,
  initiateBoxOAuth,
  connectSnowflake,
  getAllConnectionStatuses,
  disconnectService,
} from '../../services/oauthService';
import { useAdmin } from '../../context/AdminContext';

const SERVICE_CONFIG = {
  salesforce: {
    name: 'Salesforce',
    descriptionKey: 'integrations.serviceDescriptions.salesforce',
    color: '#00A1E0',
    gradient: 'from-[#00A1E0] to-[#0070D2]',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
        <path d="M10.05 2.53a4.49 4.49 0 0 1 7.25 1.5 5.48 5.48 0 0 1 4.38 7.72 4.98 4.98 0 0 1-1.78 8.48 4.49 4.49 0 0 1-7.64 1.27 4.98 4.98 0 0 1-6.36-.76A4.99 4.99 0 0 1 3.5 13.5a4.98 4.98 0 0 1 1.77-4.42A4.49 4.49 0 0 1 10.05 2.53z"/>
      </svg>
    ),
  },
  box: {
    name: 'Box',
    descriptionKey: 'integrations.serviceDescriptions.box',
    color: '#0061D5',
    gradient: 'from-[#0061D5] to-[#004BA0]',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
        <path d="M2.5 5.5L12 1l9.5 4.5v13L12 23l-9.5-4.5v-13zM12 3.3L4.5 6.8v10.4L12 20.7l7.5-3.5V6.8L12 3.3z"/>
        <path d="M12 8l5 2.5v5L12 18l-5-2.5v-5L12 8z"/>
      </svg>
    ),
  },
  snowflake: {
    name: 'Snowflake',
    descriptionKey: 'integrations.serviceDescriptions.snowflake',
    color: '#29B5E8',
    gradient: 'from-[#29B5E8] to-[#1A8CC7]',
    // Snowflake is not required to create a report — Box + Salesforce are the only
    // services the "ready to create a report" check depends on.
    optional: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
        <path d="M12 2l1.5 3.5L17 4l-1 3.5L20 8l-3.5 1L18 12l-3.5-1L16 15l-3.5-2L12 17l-.5-4L8 15l1.5-3.5L6 12l3.5-1.5L8 7l3.5 1.5L12 5V2z"/>
      </svg>
    ),
  },
};

const SnowflakeForm = ({ onConnect, isConnecting }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    account: '', username: '', password: '', warehouse: '', database: '', schema_name: 'PUBLIC', role: '',
  });
  const [authType, setAuthType] = useState('password');

  const fieldPlaceholders = {
    account: t('integrations.snowflakeForm.accountPlaceholder'),
    username: t('integrations.snowflakeForm.usernamePlaceholder'),
    warehouse: t('integrations.snowflakeForm.warehousePlaceholder'),
    database: t('integrations.snowflakeForm.databasePlaceholder'),
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData };
    if (authType === 'private_key') {
      delete payload.password;
    } else {
      delete payload.private_key;
    }
    onConnect(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-3">
      <div className="flex gap-2 mb-3">
        <button type="button" onClick={() => setAuthType('password')}
          className={`px-3 py-1.5 text-xs rounded-full font-medium transition-all ${authType === 'password' ? 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          {t('integrations.snowflakeForm.passwordTab')}
        </button>
        <button type="button" onClick={() => setAuthType('private_key')}
          className={`px-3 py-1.5 text-xs rounded-full font-medium transition-all ${authType === 'private_key' ? 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          {t('integrations.snowflakeForm.privateKeyTab')}
        </button>
      </div>
      {['account', 'username'].map((field) => (
        <input key={field} type="text" placeholder={fieldPlaceholders[field]}
          value={formData[field]} required
          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent outline-none transition-all"
        />
      ))}
      {authType === 'password' ? (
        <input type="password" placeholder={t('integrations.snowflakeForm.passwordPlaceholder')} value={formData.password || ''} required
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent outline-none transition-all"
        />
      ) : (
        <textarea placeholder={t('integrations.snowflakeForm.privateKeyPlaceholder')} rows={3} required
          onChange={(e) => setFormData({ ...formData, private_key: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent outline-none transition-all font-mono"
        />
      )}
      {['warehouse', 'database'].map((field) => (
        <input key={field} type="text" placeholder={fieldPlaceholders[field]}
          value={formData[field]} 
          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent outline-none transition-all"
        />
      ))}
      <button type="submit" disabled={isConnecting}
        className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-medium text-sm hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg">
        {isConnecting ? t('integrations.connectingButton') : t('integrations.snowflakeForm.connectButton')}
      </button>
    </form>
  );
};

const SalesforceDomainForm = ({ onConnect, isConnecting, onCancel }) => {
  const { t } = useTranslation();
  const [domainType, setDomainType] = useState('production');
  const [customDomain, setCustomDomain] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    let domain = null;
    if (domainType === 'production') {
      domain = 'login.salesforce.com';
    } else if (domainType === 'sandbox') {
      domain = 'test.salesforce.com';
    } else if (domainType === 'custom' && customDomain.trim()) {
      domain = customDomain.trim();
    }
    onConnect(domain);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-3">
      <div className="space-y-2">
        <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-all">
          <input
            type="radio"
            name="domainType"
            value="production"
            checked={domainType === 'production'}
            onChange={(e) => setDomainType(e.target.value)}
            className="text-blue-600"
          />
          <div>
            <div className="font-medium text-sm">{t('integrations.salesforceForm.production')}</div>
            <div className="text-xs text-gray-500">login.salesforce.com</div>
          </div>
        </label>
        
        <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-all">
          <input
            type="radio"
            name="domainType"
            value="sandbox"
            checked={domainType === 'sandbox'}
            onChange={(e) => setDomainType(e.target.value)}
            className="text-blue-600"
          />
          <div>
            <div className="font-medium text-sm">{t('integrations.salesforceForm.sandbox')}</div>
            <div className="text-xs text-gray-500">test.salesforce.com</div>
          </div>
        </label>
        
        <label className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-all">
          <div className="flex items-center gap-2">
            <input
              type="radio"
              name="domainType"
              value="custom"
              checked={domainType === 'custom'}
              onChange={(e) => setDomainType(e.target.value)}
              className="text-blue-600"
            />
            <div>
              <div className="font-medium text-sm">{t('integrations.salesforceForm.customDomain')}</div>
              <div className="text-xs text-gray-500">{t('integrations.salesforceForm.customDomainHint')}</div>
            </div>
          </div>
          {domainType === 'custom' && (
            <input
              type="text"
              placeholder={t('integrations.salesforceForm.customDomainPlaceholder')}
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none"
              required
            />
          )}
        </label>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-200 transition-all"
        >
          {t('integrations.salesforceForm.cancel')}
        </button>
        <button
          type="submit"
          disabled={isConnecting || (domainType === 'custom' && !customDomain.trim())}
          className="flex-1 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium text-sm hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
        >
          {isConnecting ? t('integrations.connectingButton') : t('integrations.salesforceForm.continue')}
        </button>
      </div>
    </form>
  );
};

const ServiceConnectorCards = () => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const { refreshAfterIntegrationChange } = useAdmin();
  const [searchParams] = useSearchParams();
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [expandedSnowflake, setExpandedSnowflake] = useState(false);
  const [expandedSalesforce, setExpandedSalesforce] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    // Listen for auth_success / auth_error messages from popup
    const handleMessage = (event) => {
      if (event.data === 'auth_success') {
        console.log('🎉 Auth success message received from popup!');
        setToast({ type: 'success', message: t('integrations.connectedSuccessfully') });
        fetchStatuses();
        // Refresh AdminContext so agent_ids_configured / is_agent_ready reflect
        // this newly-connected service immediately, without a full page reload.
        refreshAfterIntegrationChange();
      } else if (event.data && typeof event.data === 'object' && event.data.type === 'auth_error') {
        // Structured error from backend (e.g. Box enterprise SSO failure)
        const { service, message } = event.data;
        const serviceName = service ? service.charAt(0).toUpperCase() + service.slice(1) : t('integrations.genericServiceLabel');
        console.error(`❌ ${serviceName} auth error from popup:`, message);
        let userMessage = t('integrations.authFailedGeneric', { name: serviceName });
        // Provide a more actionable message for enterprise SSO errors
        if (message && (message.toLowerCase().includes('sso') || message.toLowerCase().includes('single sign') || message.toLowerCase().includes('ping'))) {
          userMessage = t('integrations.authFailedSSO', { name: serviceName });
        } else if (message) {
          userMessage = t('integrations.authFailedDetailed', { name: serviceName, message });
        }
        setToast({ type: 'error', message: userMessage });
        setConnecting(null);
      }
    };
    window.addEventListener('message', handleMessage);

    // Handle OAuth callback params (legacy fallback)
    const oauthService = searchParams.get('oauth');
    const oauthStatus = searchParams.get('status');
    if (oauthService && oauthStatus) {
      const oauthServiceName = oauthService.charAt(0).toUpperCase() + oauthService.slice(1);
      if (oauthStatus === 'success') {
        setToast({ type: 'success', message: t('integrations.oauthConnectedSuccess', { name: oauthServiceName }) });
      } else {
        setToast({ type: 'error', message: t('integrations.oauthConnectFailedRetry', { name: oauthServiceName }) });
      }
      // Clear params after handling
      window.history.replaceState({}, document.title, '/integrations');
      // Re-fetch statuses after OAuth callback
      setTimeout(() => fetchStatuses(), 500);
      if (oauthStatus === 'success') {
        refreshAfterIntegrationChange();
      }
    }

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    console.log('🔍 ServiceConnectorCards - Auth State:', { 
      authenticated: keycloak.authenticated, 
      hasToken: !!keycloak.token,
      initialized: keycloak.authenticated !== undefined
    });
    
    if (keycloak.authenticated && keycloak.token) {
      fetchStatuses();
    } else if (keycloak.authenticated === false) {
      // If we're definitely not authenticated, stop loading
      setLoading(false);
    }
  }, [keycloak.authenticated, keycloak.token]);

  const fetchStatuses = async () => {
    try {
      console.log('🔄 Fetching connection statuses...');
      setLoading(true);
      
      if (!keycloak.token) {
        console.warn('⚠️ No token available, skipping fetch');
        setLoading(false);
        return;
      }

      const data = await getAllConnectionStatuses(keycloak.token);
      console.log('✅ Connection statuses received:', data);
      setStatuses(data);
    } catch (err) {
      console.error('❌ Failed to fetch connection statuses:', err);
      // Fallback to empty statuses on error
      setStatuses({});
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (serviceType) => {
    // For Salesforce, show domain picker first
    if (serviceType === 'salesforce') {
      setExpandedSalesforce(true);
      return;
    }

    setConnecting(serviceType);
    
    // Open the popup immediately to preserve user gesture
    const width = 600, height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const popupName = `OAuth_${serviceType}_${Date.now()}`;
    const popup = window.open('about:blank', popupName, `width=${width},height=${height},top=${top},left=${left}`);
    
    try {
      let authUrl = null;
      if (serviceType === 'box') {
        authUrl = await initiateBoxOAuth(keycloak.token);
      } else if (serviceType === 'snowflake') {
        popup?.close();
        setExpandedSnowflake(true);
        setConnecting(null);
        return;
      }

      if (authUrl && popup) {
        popup.location.href = authUrl;
        setConnecting(null);
      } else if (popup) {
        popup.close();
        setConnecting(null);
      }
    } catch (err) {
      if (popup) popup.close();
      console.error(`Failed to connect ${serviceType}:`, err);
      setToast({ type: 'error', message: t('integrations.connectFailedGeneric', { name: SERVICE_CONFIG[serviceType]?.name || serviceType, message: err.response?.data?.detail || err.message }) });
      setConnecting(null);
    }
  };

  const handleSalesforceConnect = async (domain) => {
    setConnecting('salesforce');
    setExpandedSalesforce(false);
    
    // Open the popup immediately to preserve user gesture
    const width = 600, height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const popupName = `OAuth_salesforce_${Date.now()}`;
    const popup = window.open('about:blank', popupName, `width=${width},height=${height},top=${top},left=${left}`);
    
    try {
      const authUrl = await initiateSalesforceOAuth(keycloak.token, domain);
      
      if (authUrl && popup) {
        popup.location.href = authUrl;
        setConnecting(null);
      } else if (popup) {
        popup.close();
        setConnecting(null);
      }
    } catch (err) {
      if (popup) popup.close();
      console.error('Failed to connect Salesforce:', err);
      setToast({ type: 'error', message: t('integrations.connectFailedGeneric', { name: 'Salesforce', message: err.response?.data?.detail || err.message }) });
      setConnecting(null);
    }
  };

  const handleSnowflakeConnect = async (credentials) => {
    setConnecting('snowflake');
    try {
      await connectSnowflake(credentials, keycloak.token);
      setToast({ type: 'success', message: t('integrations.snowflakeConnectedSuccess') });
      setExpandedSnowflake(false);
      await fetchStatuses();
      // Refresh AdminContext so agent_ids_configured / is_agent_ready reflect
      // this newly-connected service immediately, without a full page reload.
      await refreshAfterIntegrationChange();
    } catch (err) {
      console.error('Snowflake connect failed:', err);
      setToast({ type: 'error', message: t('integrations.snowflakeConnectFailed', { message: err.response?.data?.detail || err.message }) });
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (serviceType) => {
    if (!window.confirm(t('integrations.confirmDisconnect', { service: SERVICE_CONFIG[serviceType].name }))) return;
    try {
      await disconnectService(serviceType, keycloak.token);
      setToast({ type: 'success', message: t('integrations.disconnectedSuccess', { name: SERVICE_CONFIG[serviceType].name }) });
      await fetchStatuses();
      // Keep AdminContext in sync so the "not connected" warning reappears
      // as soon as a required service is disconnected.
      await refreshAfterIntegrationChange();
    } catch (err) {
      console.error(`Failed to disconnect ${serviceType}:`, err);
      setToast({ type: 'error', message: t('integrations.disconnectFailedGeneric', { message: err.message }) });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="text-gray-500 text-sm font-medium">{t('integrations.loadingConnections')}</span>
        </div>
        <button 
          onClick={fetchStatuses}
          className="text-xs text-blue-500 hover:text-blue-600 underline"
        >
          {t('integrations.retryLoading')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium transition-all duration-300 animate-slide-in
          ${toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.message}
        </div>
      )}

      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t('integrations.serviceConnections', { defaultValue: 'Service Connections' })}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(SERVICE_CONFIG).map(([key, config]) => {
          const status = statuses[key] || { connected: false };
          const isConnecting = connecting === key;

          return (
            <div key={key}
              className={`relative rounded-2xl border transition-all duration-300 overflow-hidden
                ${status.connected 
                  ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-md' 
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'}`}
            >
              {/* Status indicator dot */}
              <div className={`absolute top-4 right-4 w-3 h-3 rounded-full transition-all
                ${status.connected ? 'bg-emerald-400 shadow-sm shadow-emerald-200' : 'bg-gray-300'}
                ${status.connected ? 'animate-pulse' : ''}`}
              />

              <div className="p-5">
                {/* Icon + Name */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${config.gradient} text-white shadow-md`}>
                    {config.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-bold text-gray-900">{config.name}</h4>
                      {config.optional && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px] font-medium uppercase tracking-wide">
                          {t('integrations.optional')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{t(config.descriptionKey)}</p>
                  </div>
                </div>

                {/* Status info */}
                {status.connected && (
                  <div className="mb-3 p-2.5 bg-emerald-50 rounded-lg border border-emerald-100">
                    <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-medium">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('integrations.connected')}
                    </div>
                    {status.connected_at && (
                      <p className="text-[10px] text-emerald-600 mt-1">
                        {t('integrations.since', { date: new Date(status.connected_at).toLocaleDateString() })}
                      </p>
                    )}
                    {status.instance_url && (
                      <p className="text-[10px] text-emerald-600 mt-0.5 truncate" title={status.instance_url}>
                        {status.instance_url}
                      </p>
                    )}
                  </div>
                )}

                {!status.connected && (status.status === 'needs_reconnect' || status.status === 'config_error') && (
                  <div className="mb-3 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-amber-800 text-xs font-medium">
                      {status.status === 'needs_reconnect'
                        ? t('integrations.reconnectNeeded', { name: config.name })
                        : t('integrations.configErrorMessage', { name: config.name })}
                    </p>
                    {status.last_error_at && (
                      <p className="text-[10px] text-amber-700 mt-1">
                        {t('integrations.since', { date: new Date(status.last_error_at).toLocaleString() })}
                      </p>
                    )}
                  </div>
                )}

                {/* Snowflake expanded form */}
                {key === 'snowflake' && expandedSnowflake && !status.connected && (
                  <SnowflakeForm onConnect={handleSnowflakeConnect} isConnecting={isConnecting} />
                )}

                {/* Salesforce domain picker */}
                {key === 'salesforce' && expandedSalesforce && !status.connected && (
                  <SalesforceDomainForm 
                    onConnect={handleSalesforceConnect} 
                    isConnecting={isConnecting}
                    onCancel={() => setExpandedSalesforce(false)}
                  />
                )}

                {/* Action button */}
                {!status.connected && !(key === 'snowflake' && expandedSnowflake) && !(key === 'salesforce' && expandedSalesforce) ? (
                  <button onClick={() => handleConnect(key)} disabled={isConnecting}
                    className={`w-full py-2.5 rounded-xl font-medium text-sm text-white transition-all
                      bg-gradient-to-r ${config.gradient} hover:shadow-lg hover:scale-[1.02]
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}>
                    {isConnecting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        {t('integrations.connectingButton')}
                      </span>
                    ) : status.status === 'needs_reconnect' ? (
                      t('integrations.reconnectButton', { name: config.name })
                    ) : (
                      t('integrations.connectButton', { name: config.name })
                    )}
                  </button>
                ) : status.connected ? (
                  <button onClick={() => handleDisconnect(key)}
                    className="w-full py-2 rounded-xl font-medium text-xs text-red-500 bg-red-50 border border-red-100 hover:bg-red-100 transition-all">
                    {t('integrations.disconnectButton')}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceConnectorCards;
